import { TPathPattern, IPathMatchResult } from '../../types/simple';

/**
 * 路径匹配工具类
 */
export class PathMatcher {
  /**
   * 检查路径是否匹配指定的模式
   * @param currentPath 当前页面路径
   * @param pathPattern 路径匹配模式
   * @returns 匹配结果
   */
  static match(currentPath: string, pathPattern?: TPathPattern): IPathMatchResult {
    if (!pathPattern) {
      return {
        matched: true,
        matchedPath: currentPath
      };
    }

    // 数组模式：任意一个匹配即可
    if (Array.isArray(pathPattern)) {
      for (const pattern of pathPattern) {
        const result = this.matchSingle(currentPath, pattern);
        if (result.matched) {
          return result;
        }
      }
      return {
        matched: false,
        matchedPath: currentPath
      };
    }

    // 单个模式匹配
    return this.matchSingle(currentPath, pathPattern);
  }

  /**
   * 检查路径是否被排除
   * @param currentPath 当前页面路径
   * @param excludePaths 排除路径模式
   * @returns 是否被排除
   */
  static isExcluded(currentPath: string, excludePaths?: TPathPattern): boolean {
    if (!excludePaths) {
      return false;
    }

    const result = this.match(currentPath, excludePaths);
    return result.matched;
  }

  /**
   * 检查预设选择器是否应该在当前路径生效
   * @param currentPath 当前页面路径
   * @param pathPattern 包含路径模式
   * @param excludePaths 排除路径模式
   * @returns 是否应该生效
   */
  static shouldApply(
    currentPath: string, 
    pathPattern?: TPathPattern, 
    excludePaths?: TPathPattern
  ): boolean {
    // 检查排除规则
    if (this.isExcluded(currentPath, excludePaths)) {
      return false;
    }

    // 检查包含规则
    const matchResult = this.match(currentPath, pathPattern);
    return matchResult.matched;
  }

  /**
   * 单个模式匹配
   * @param currentPath 当前路径
   * @param pattern 匹配模式
   * @returns 匹配结果
   */
  private static matchSingle(currentPath: string, pattern: string | RegExp): IPathMatchResult {
    // 正则表达式匹配
    if (pattern instanceof RegExp) {
      const matched = pattern.test(currentPath);
      return {
        matched,
        matchedPattern: pattern,
        matchedPath: currentPath
      };
    }

    // 字符串模式匹配
    const stringPattern = pattern as string;
    
    // 精确匹配
    if (stringPattern === currentPath) {
      return {
        matched: true,
        matchedPattern: pattern,
        matchedPath: currentPath
      };
    }

    // 通配符匹配
    if (stringPattern.includes('*') || stringPattern.includes('?')) {
      const matched = this.matchWildcard(currentPath, stringPattern);
      return {
        matched,
        matchedPattern: pattern,
        matchedPath: currentPath
      };
    }

    // 前缀匹配（兼容性）
    if (currentPath.includes(stringPattern)) {
      return {
        matched: true,
        matchedPattern: pattern,
        matchedPath: currentPath
      };
    }

    return {
      matched: false,
      matchedPath: currentPath
    };
  }

  /**
   * 通配符匹配
   * @param path 路径
   * @param pattern 通配符模式
   * @returns 是否匹配
   */
  private static matchWildcard(path: string, pattern: string): boolean {
    // 检查原始模式以确定 * 的语义
    const endsWithStar = pattern.endsWith('*') && !pattern.endsWith('**');
    const endsWithSlashStar = pattern.endsWith('/*');
    
    // 转换通配符模式为正则表达式
    let regexPattern = pattern
      .replace(/\*\*/g, '§DOUBLE_STAR§')     // 临时标记 **
      .replace(/\*/g, '§SINGLE_STAR§')       // 临时标记 *
      .replace(/\?/g, '§QUESTION§')          // 临时标记 ?
      .replace(/[.+^${}()|[\]\\]/g, '\\$&')  // 转义正则特殊字符
      .replace(/§DOUBLE_STAR§/g, '.*')       // ** 匹配任意字符（包括 /）
      .replace(/§QUESTION§/g, '[^/]');       // ? 匹配单个非 / 字符

    // 处理单个 * 的情况：
    if (endsWithSlashStar) {
      // 如果是 /*, 应该只匹配单级目录
      regexPattern = regexPattern.replace(/§SINGLE_STAR§/g, '[^/]*');
    } else if (endsWithStar) {
      // 如果以 * 结尾（但不是 /**），则匹配任意字符（用于前缀匹配）
      regexPattern = regexPattern.replace(/§SINGLE_STAR§/g, '.*');
    } else {
      // 其他情况，* 只匹配单个目录级别
      regexPattern = regexPattern.replace(/§SINGLE_STAR§/g, '[^/]*');
    }

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(path);
  }

  /**
   * 获取当前页面路径
   * @returns 当前页面路径
   */
  static getCurrentPath(): string {
    if (typeof window === 'undefined') {
      return '/';
    }
    
    return window.location.pathname;
  }

  /**
   * 标准化路径（移除尾部斜杠，确保以 / 开头）
   * @param path 原始路径
   * @returns 标准化后的路径
   */
  static normalizePath(path: string): string {
    if (!path) {
      return '/';
    }

    // 确保以 / 开头
    let normalized = path.startsWith('/') ? path : `/${path}`;
    
    // 移除尾部斜杠（除非是根路径）
    if (normalized.length > 1 && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }

    return normalized;
  }
}

/**
 * 路径匹配缓存
 */
export class PathMatchCache {
  private static cache = new Map<string, boolean>();
  private static maxCacheSize = 1000;

  /**
   * 生成缓存键
   */
  private static generateCacheKey(
    path: string,
    pathPattern?: TPathPattern,
    excludePaths?: TPathPattern
  ): string {
    const patternStr = this.patternToString(pathPattern);
    const excludeStr = this.patternToString(excludePaths);
    return `${path}|${patternStr}|${excludeStr}`;
  }

  /**
   * 将模式转换为字符串
   */
  private static patternToString(pattern?: TPathPattern): string {
    if (!pattern) return '';
    if (typeof pattern === 'string') return pattern;
    if (pattern instanceof RegExp) return pattern.toString();
    if (Array.isArray(pattern)) return pattern.join(',');
    return '';
  }

  /**
   * 获取缓存的匹配结果
   */
  static getCached(
    path: string,
    pathPattern?: TPathPattern,
    excludePaths?: TPathPattern
  ): boolean | null {
    const key = this.generateCacheKey(path, pathPattern, excludePaths);
    return this.cache.get(key) ?? null;
  }

  /**
   * 设置缓存
   */
  static setCached(
    path: string,
    pathPattern: TPathPattern | undefined,
    excludePaths: TPathPattern | undefined,
    result: boolean
  ): void {
    // 限制缓存大小
    if (this.cache.size >= this.maxCacheSize) {
      // 清除最旧的一半缓存
      const entries = Array.from(this.cache.entries());
      const toDelete = entries.slice(0, Math.floor(this.maxCacheSize / 2));
      toDelete.forEach(([key]) => this.cache.delete(key));
    }

    const key = this.generateCacheKey(path, pathPattern, excludePaths);
    this.cache.set(key, result);
  }

  /**
   * 清除缓存
   */
  static clear(): void {
    this.cache.clear();
  }
} 